<template>
  <div class="w-full order-1 md:order-2">
    <div class="bg-white p-6 rounded-lg shadow mb-6">
      <h3 class="text-sm text-center font-semibold mb-2">{{ $t('reservationSummary.title') }}</h3>
      <h4 class="text-xs text-center font-semibold text-gray-500 mb-4">{{ $t('reservationSummary.orderId') }} : {{
        orderId }}</h4>

      <!-- Check-in dan Check-out -->
      <div class="text-sm text-gray-600 border rounded-lg p-4 mb-4">
        <div class="flex justify-between items-start p-4 border border-gray-300 rounded-lg">
          <!-- Check-in Section -->
          <div class="w-1/2">
            <span class="block font-semibold text-gray-500">{{ $t('reservationSummary.checkIn') }}</span>
            <div class="flex flex-col gap-1">
              <span class="text-gray-800 font-semibold">{{ $formatters.formatShortDate(bookingSummary.CheckInDate)
                }}</span>
              <span class="text-blue-500 text-xs pl-1">{{ $t('reservationSummary.from') }} {{
                $formatters.formatTime(bookingSummary.CheckInTime) }}</span>
            </div>
          </div>

          <!-- Border separator between Check-in and Check-out -->
          <div class="w-px bg-gray-300 h-full mx-2"></div>

          <!-- Check-out Section -->
          <div class="w-1/2">
            <span class="block font-semibold text-gray-500 pl-4">{{ $t('reservationSummary.checkOut') }}</span>
            <div class="border-l pl-4 flex flex-col gap-1">
              <span class="text-gray-800 font-semibold">{{ $formatters.formatShortDate(bookingSummary.CheckOutDate)
                }}</span>
              <span class="text-blue-500 text-xs pl-1">{{ $t('reservationSummary.to') }} {{
                $formatters.formatTime(bookingSummary.CheckOutTime) }}</span>
            </div>
          </div>
        </div>
        <div v-if="bookingSummary.Period == 1" class="flex flex-col gap-2 mt-4 text-gray-600">
          <span>{{ $t('reservationSummary.totalLengthOfStay') }}</span>
          <div class="flex flex-row items-center gap-2">
            <span class="font-semibold">{{ bookingSummary.TotalNights }} {{ bookingSummary.TotalNights > 1 ?
              $t('reservationSummary.nights') : $t('reservationSummary.night') }}</span>
            <AppIcon icon="mdi:calendar-month-outline" class="w-5 h-5" />
          </div>
        </div>
        <div v-if="bookingSummary.Period == 2" class="flex flex-col gap-2 mt-4 text-gray-600">
          <span>{{ $t('reservationSummary.totalLengthOfStay') }}</span>
          <div v-if="bookingSummary?.BookDetails && bookingSummary?.BookDetails?.length > 0" class="flex flex-row items-center gap-2">
            <span class="font-semibold">{{ bookingSummary?.BookDetails[0]?.TotalMonths }} {{ bookingSummary?.BookDetails[0]?.TotalMonths > 1 ?
              $t('reservationSummary.months') : $t('reservationSummary.month') }}</span>
            <AppIcon icon="mdi:calendar-month-outline" class="w-5 h-5" />
          </div>
        </div>
        <!-- CH-W17 - info penyesuaian jam mulai sewa (transisi harian -> bulanan) -->
        <div v-if="isTransitionAdjusted" class="flex items-start gap-2 mt-4 rounded-lg bg-blue-50 p-3 text-xs text-blue-700">
          <AppIcon icon="mdi:information-outline" class="w-4 h-4 mt-0.5 shrink-0" />
          <span>{{ $t("reservationSummary.transitionAdjusted") }}</span>
        </div>
      </div>

      <!-- Price Summary -->
      <h4 class="text-lg font-semibold mt-6 mb-4">{{ $t('reservationSummary.yourPriceSummary') }}</h4>
      <div class="space-y-4">
        <div v-for="(detail, index) in bookingDetail" :key="detail.Id" class="border rounded-lg shadow-sm p-4">
          <!-- Header (Collapsible Trigger) -->
          <div @click="toggleCollapse(index)" class="flex justify-between items-center cursor-pointer rounded-lg">
            <!-- Nama Kamar -->
            <h2 class="text-base font-semibold text-gray-700">{{ getPriceSummaryTitle(detail, index) }}</h2>

            <!-- Total Harga -->
            <div class="text-sm font-semibold text-gray-500 flex items-center gap-2">
              <span class="text-gray-900">{{ $formatters.formatCurrency(detail.Total) }}</span>
              <AppIcon :icon="collapsedIndex === index ? 'mdi:chevron-up' : 'mdi:chevron-down'"
                class="w-5 h-5 text-gray-500" />
            </div>
          </div>

          <!-- Collapsible Content -->
          <div v-if="collapsedIndex === index" class="mt-4 space-y-2 text-sm text-gray-600">
            <!-- Price Details -->
            <div v-if="bookingSummary?.Period == 1" class="flex justify-between">
              <span>{{ $t('reservationSummary.pricePerNight', {
                price: $formatters.formatCurrency(detail.PricePerNight),
                nights: detail.TotalNights
              }) }}</span>
              <span class="font-semibold">{{ $formatters.formatCurrency(detail.SubTotal) }}</span>
            </div>

            <div v-if="bookingSummary?.Period == 2" class="space-y-1">
              <div class="flex justify-between">
                <span>{{ periodLabelForDetail(detail, false) }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(detail.SubTotal) }}</span>
              </div>
              <div v-if="showComparisonPriceForDetail(detail)" class="flex justify-between text-gray-500">
                <span>{{ periodComparisonLabelForDetail(detail) }}</span>
                <span class="line-through">{{ $formatters.formatCurrency(getComparisonPriceForDetail(detail)) }}</span>
              </div>
              <div v-else class="text-xs text-gray-500">
                {{ $t('propertyDetail.comparisonPriceUnavailable') }}
              </div>
            </div>

            <!-- Deposit -->
            <div v-if="detail.BookDetailDeposit" class="flex justify-between">
              <span>{{ $t('reservationSummary.deposit') }}</span>
              <span class="font-semibold">{{ $formatters.formatCurrency(detail.BookDetailDeposit.Total) }}</span>
            </div>

            <!-- Service Fee -->
            <div v-if="detail.BookDetailCharges && detail.BookDetailCharges.length > 0">
              <div v-for="charge in detail.BookDetailCharges.filter(e => !e.IsPPN)" :key="charge.Id" class="flex justify-between">
                <span>{{ charge.Name }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(charge.Total) }}</span>
              </div>
            </div>

            <!-- PPN -->
            <div v-if="detail.BookDetailCharges && detail.BookDetailCharges.length > 0">
              <div v-for="charge in detail.BookDetailCharges.filter(e => e.IsPPN)" :key="charge.Id" class="flex justify-between">
                <span>{{ charge.Name }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(charge.Total) }}</span>
              </div>
            </div>
 

            <!-- Tambahkan Rounding Jika Lebih dari 0.00 -->
            <div v-if="parseFloat(rounding) > 0.00"
              class="flex justify-between items-center text-sm text-gray-600 mt-2">
              <span>{{ $t('reservationSummary.rounding') }}</span>
              <span class="font-semibold">{{ $formatters.formatCurrency(parseFloat(rounding)) }}</span>
            </div>

            <!-- BookDetailAddOns -->
            <div v-if="detail.BookDetailAddOns && detail.BookDetailAddOns.length > 0" class="mt-4">
              <h5 class="text-sm font-semibold text-gray-500 mb-2">{{ $t('reservationSummary.addOns') }}</h5>
              <div v-for="addon in detail.BookDetailAddOns" :key="addon.Id" class="flex justify-between">
                <span>{{ addon.Name || addon.RoomAddOn?.AddOn?.Name }} x{{ addon.Quantity }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(addon.Total) }}</span>
              </div>
            </div>

            <!-- Separator -->
            <hr class="my-2" />

            <!-- Subtotal -->
            <div class="flex justify-between text-gray-500">
              <span>{{ $t('reservationSummary.subtotal') }}</span>
              <span class="font-semibold">{{ $formatters.formatCurrency(detail.Total) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Tambahkan Total-->
      <div class="mt-6">

        <div v-if="bookingSummary.BookVouchers && bookingSummary.BookVouchers.length > 0">
            <div v-for="voucher in bookingSummary.BookVouchers" :key="voucher.Id" class="flex justify-between">
              <span class="text-sm">{{ voucher.VoucherCode }}</span>
              <span class="font-semibold text-sm">-{{ $formatters.formatCurrency(voucher.FinalDiscountAmount) }}</span>
            </div>
          </div>
        <div class="flex justify-between items-center text-lg font-bold text-blue-500">
          <span>{{ $t('reservationSummary.total') }}</span>
          <span>{{ $formatters.formatCurrency(grandTotal) }}</span>
        </div>
      </div>
    </div>

    <!-- <div class="flex flex-row justify-center w-full">
      <p class="text-xs text-gray-500">
        Dapatkan <span class="text-blue-500 font-semibold">+{{ $formatters.formatCurrency(bookingDetail.points) }}
          poin</span> setelah transaksi
      </p>
    </div> -->
  </div>
</template>

<script>
export default {
  name: 'ReservationSummary',
  data() {
    return {
      collapsedIndex: null, // Index item yang sedang di-expand
    }
  },
  props: {
    orderId: String,
    bookingDetail: Array,
    bookingSummary: Object,
    selectedAddons: Array,
    selectedAddonsDetails: Array,
    voucherApplied: Boolean,
    discountAmount: Number,
    usePoints: Boolean,
    pointsToUse: Number,
    grandTotal: Number,
    rounding: String,
  },
  computed: {
    // CH-W17 - true bila start bulanan digeser karena transisi harian -> bulanan
    isTransitionAdjusted() {
      const details = (Array.isArray(this.bookingDetail) && this.bookingDetail.length)
        ? this.bookingDetail
        : (this.bookingSummary?.BookDetails || []);
      return details.some((d) => d?.IsTransitionAdjusted === true);
    },
  },
  methods: {
    getYearDaysFromBooking() {
      const checkIn = this.bookingSummary?.CheckInDate ? new Date(this.bookingSummary.CheckInDate) : new Date();
      if (Number.isNaN(checkIn.getTime())) return 365;
      const year = checkIn.getFullYear();
      return ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)) ? 366 : 365;
    },
    getMonthDaysFromBooking() {
      const checkIn = this.bookingSummary?.CheckInDate ? new Date(this.bookingSummary.CheckInDate) : new Date();
      if (Number.isNaN(checkIn.getTime())) return 30;
      return new Date(checkIn.getFullYear(), checkIn.getMonth() + 1, 0).getDate();
    },
    getDailyUnitPrice(detail) {
      const byField = Number(detail?.PricePerNight || 0);
      if (Number.isFinite(byField) && byField > 0) return byField;

      const totalNights = Number(detail?.TotalNights || 0);
      const subTotal = Number(detail?.SubTotal || 0);
      if (totalNights > 0 && subTotal > 0) return subTotal / totalNights;

      return 0;
    },
    isYearlyDetail(detail) {
      return Number(detail?.TotalMonths || 0) === 12;
    },
    getFinalUnitPriceForDetail(detail) {
      const totalMonths = Number(detail?.TotalMonths || 0);
      const subTotal = Number(detail?.SubTotal || 0);
      if (this.isYearlyDetail(detail)) return subTotal;
      if (totalMonths > 0) return subTotal / totalMonths;
      return subTotal;
    },
    getComparisonPriceForDetail(detail) {
      const dailyPrice = this.getDailyUnitPrice(detail);
      if (dailyPrice <= 0) return 0;

      if (this.isYearlyDetail(detail)) {
        return dailyPrice * this.getYearDaysFromBooking();
      }
      return dailyPrice * this.getMonthDaysFromBooking();
    },
    showComparisonPriceForDetail(detail) {
      return this.getComparisonPriceForDetail(detail) > 0;
    },
    periodLabelForDetail(detail, withTotal = true) {
      const finalUnit = this.getFinalUnitPriceForDetail(detail);
      if (this.isYearlyDetail(detail)) {
        return `${this.$formatters.formatCurrency(finalUnit)} / ${this.$t('reservationSummary.year')}`;
      }

      const totalMonths = Number(detail?.TotalMonths || 0);
      if (withTotal) {
        return this.$t('reservationSummary.pricePerMonth', {
          price: this.$formatters.formatCurrency(finalUnit),
          months: totalMonths,
        });
      }
      return `${this.$formatters.formatCurrency(finalUnit)} / ${this.$t('reservationSummary.month')}`;
    },
    periodComparisonLabelForDetail(detail) {
      const dailyPrice = this.getDailyUnitPrice(detail);
      if (dailyPrice <= 0) return this.$t('propertyDetail.comparisonPriceUnavailable');

      if (this.isYearlyDetail(detail)) {
        return `${this.$formatters.formatCurrency(dailyPrice)} × ${this.getYearDaysFromBooking()} ${this.$t('reservationSummary.nights')}`;
      }

      return `${this.$formatters.formatCurrency(dailyPrice)} × ${this.getMonthDaysFromBooking()} ${this.$t('reservationSummary.nights')}`;
    },
    toggleCollapse(index) {
      this.collapsedIndex = this.collapsedIndex === index ? null : index;
    },
    getChargesTotal(charges) {
      return charges.reduce((total, charge) => total + charge.Total, 0);
    },
    getDetailGroupKey(detail) {
      return (
        detail?.RoomTypeId ||
        detail?.Room?.RoomTypeId ||
        detail?.RoomTypeName ||
        detail?.Room?.Name ||
        detail?.Id
      );
    },
    getDetailBaseName(detail) {
      return (
        detail?.Room?.Name ||
        detail?.RoomName ||
        detail?.RoomTypeName ||
        "Room"
      );
    },
    getPriceSummaryTitle(detail, index) {
      const baseName = this.getDetailBaseName(detail);
      const key = this.getDetailGroupKey(detail);
      const details = Array.isArray(this.bookingDetail) ? this.bookingDetail : [];

      const totalInGroup = details.filter((item) => this.getDetailGroupKey(item) === key).length;
      if (totalInGroup <= 1) return baseName;

      const position = details
        .slice(0, index + 1)
        .filter((item) => this.getDetailGroupKey(item) === key).length;

      return `${baseName} #${position}`;
    },
  }
};
</script>

<style scoped>
/* Add any additional styling here if needed */
.collapsible-header {
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  transition: background-color 0.2s ease-in-out;
}

.collapsible-header:hover {
  background-color: #f3f4f6;
}

.collapsible-content {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}
</style>
